import { FIRST_STEP_ID, isTourStepId, type TourStepId } from './tourSteps';

/**
 * Progression du parcours d'onboarding.
 *
 * Deux niveaux, volontairement distincts :
 * - le NAVIGATEUR (ce fichier) retient l'étape en cours, pour reprendre après
 *   un rechargement ;
 * - le SERVEUR (`users.onboarding_tour_closed_at`, exposé par
 *   `GET /cabinet-members/me`) retient que l'utilisateur a terminé ou fermé le
 *   parcours. C'est lui qui garantit « plus jamais d'office », quel que soit
 *   le poste.
 */

export type TourStatus = 'active' | 'completed' | 'dismissed';

export interface TourProgress {
  status: TourStatus;
  stepId: TourStepId;
  /**
   * Relance volontaire depuis la page Aide. Un parcours relancé à la main
   * s'affiche même si le serveur le dit déjà clos ; un parcours démarré
   * d'office, jamais.
   */
  manual?: boolean;
}

type ProgressStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const KEY_PREFIX = 'hector.onboardingTour.v1:';
const listeners = new Set<() => void>();

function storageKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

function defaultStorage(): ProgressStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // Navigation privée ou stockage bloqué : le parcours redémarre à chaque
    // chargement, sans planter l'application.
    return null;
  }
}

function isTourStatus(value: unknown): value is TourStatus {
  return value === 'active' || value === 'completed' || value === 'dismissed';
}

/** Renvoie `null` quand rien n'est mémorisé ou que la valeur est illisible. */
export function readTourProgress(
  userId: string,
  storage: ProgressStorage | null = defaultStorage(),
): TourProgress | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { status, stepId } = parsed as Record<string, unknown>;
    // Une étape renommée ou supprimée depuis l'écriture repart du début.
    if (!isTourStatus(status) || !isTourStepId(stepId)) return null;
    const { manual } = parsed as Record<string, unknown>;
    return manual === true ? { status, stepId, manual: true } : { status, stepId };
  } catch {
    return null;
  }
}

export function writeTourProgress(
  userId: string,
  progress: TourProgress,
  storage: ProgressStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(storageKey(userId), JSON.stringify(progress));
  } catch {
    // Quota dépassé ou stockage bloqué : la progression n'est pas mémorisée.
  }
  listeners.forEach((listener) => listener());
}

/** Relance volontaire depuis la page Aide : le parcours repart de la première étape. */
export function resetTourProgress(
  userId: string,
  storage: ProgressStorage | null = defaultStorage(),
): void {
  writeTourProgress(userId, { status: 'active', stepId: FIRST_STEP_ID, manual: true }, storage);
}

/**
 * Le parcours ne démarre de lui-même que pour les comptes créés à partir de
 * cette date (minuit, heure de Paris). Décision produit du 2026-10-01 : ne pas
 * l'imposer aux utilisateurs déjà installés.
 */
export const ONBOARDING_TOUR_LAUNCH_AT = '2026-10-05T00:00:00+02:00';

/**
 * À qui le parcours se propose de lui-même.
 * - `new-accounts` : comptes créés depuis `ONBOARDING_TOUR_LAUNCH_AT`. Les
 *   utilisateurs installés ne voient rien. C'est le réglage de PRODUCTION.
 * - `everyone` : tout utilisateur qui ne l'a pas encore terminé ni fermé,
 *   quelle que soit l'ancienneté de son compte.
 */
export type TourAudience = 'new-accounts' | 'everyone';

/**
 * Réglage de PRODUCTION : seuls les nouveaux comptes voient le parcours
 * démarrer de lui-même. `staging` porte le même code (étape 1 de #2666) : pour
 * une recette, forcer un compte avec le feature_flag `beta_onboarding_tour`.
 * Ne jamais passer cette valeur à `'everyone'`, elle imposerait le parcours à
 * tous les clients existants (issue #1840).
 */
export const ONBOARDING_TOUR_AUDIENCE: TourAudience = 'new-accounts';

/**
 * Le parcours doit-il démarrer de lui-même pour cet utilisateur ?
 *
 * @param userCreatedAt - Date de création du COMPTE (session Supabase), pas la
 *   date d'arrivée dans le cabinet : un client existant qui rejoint un second
 *   cabinet ne doit pas revoir le parcours.
 * @param forced - Drapeau `beta_onboarding_tour` : rend éligible un compte
 *   antérieur à la date de lancement (démos, tests internes).
 * @param closedOnServer - L'utilisateur a déjà terminé ou fermé le parcours.
 * @param audience - Voir `TourAudience`.
 */
export function shouldAutoStartTour(input: {
  userCreatedAt: string | undefined;
  forced: boolean;
  closedOnServer: boolean;
  audience: TourAudience;
}): boolean {
  if (input.closedOnServer) return false;
  if (input.forced || input.audience === 'everyone') return true;
  if (!input.userCreatedAt) return false;
  const created = new Date(input.userCreatedAt).getTime();
  if (Number.isNaN(created)) return false;
  return created >= new Date(ONBOARDING_TOUR_LAUNCH_AT).getTime();
}

/**
 * La progression locale doit-elle s'afficher ?
 *
 * @param closedOnServer - `undefined` tant que `GET /cabinet-members/me` n'a
 *   pas répondu : on n'affiche rien plutôt que de montrer une bulle à un
 *   utilisateur qui a fermé le parcours sur un autre poste.
 */
export function isTourVisible(
  progress: TourProgress | null,
  closedOnServer: boolean | undefined,
): boolean {
  if (progress?.status !== 'active') return false;
  if (progress.manual) return true;
  return closedOnServer === false;
}

/** Abonnement des composants React aux changements de progression. */
export function subscribeTourProgress(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
